import React, { useState, useEffect, useRef, useCallback } from 'react'
import Order from '../../models/Order'
import PreviewImage from '../PreviewImage'
import Button from '../Button'
import Spinner from '../Spinner'
import Typography from '../Typography'
import styles from './FullResDownloads.module.sass'

const getItemKey = (image) => {
  const isPose = image.resourceIdentifier?.type === 'poses'
  return `${isPose ? 'pose' : 'student_image'}-${image.id}`
}

const buildItemsFromImages = (images) => {
  return images.map((image) => {
    const isPose = image.resourceIdentifier?.type === 'poses'
    return { type: isPose ? 'pose' : 'student_image', id: image.id }
  })
}

const getDownloadUrl = (image, orderPackage, background, email) => {
  if (!image) return ''

  const isPose = image.resourceIdentifier?.type === 'poses'

  if (isPose) {
    const baseUrl = image.isPNG && image.isPNG() ? '/download/download_png_image' : '/download/download_image'
    return `${baseUrl}/${image.studentImage.studentId}?pose_id=${image.id}&background_id=${background && background.id}&order_package_id=${orderPackage.id}&email=${email}${image.foregroundId ? `&foreground_id=${image.foregroundId}` : ''}`
  } else {
    const baseUrl = image.isPNG && image.isPNG() ? '/download/download_png_image' : '/download/download_image'
    return `${baseUrl}/${image.studentId}?student_image_id=${image.id}${background ? `&background_id=${background.id}` : ''}&order_package_id=${orderPackage.id}&email=${email}`
  }
}

export default function FullResDownloads({ orderId, email }) {
  const [images, setImages] = useState([])
  const [backgrounds, setBackgrounds] = useState([])
  const [orderPackages, setOrderPackages] = useState([])
  const [availability, setAvailability] = useState({})
  const [isCheckingAvailability, setIsCheckingAvailability] = useState(true)
  const [hasLoaded, setHasLoaded] = useState(false)
  const pollingRef = useRef(null)

  const checkAvailability = useCallback((items) => {
    fetch('/download/check_image_availability', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ order_id: orderId, email, items }),
    })
      .then((res) => res.json())
      .then((data) => {
        setAvailability(data.availability || {})
        setIsCheckingAvailability(false)

        const allReady = Object.values(data.availability || {}).every((v) => v === true)
        if (allReady && pollingRef.current) {
          clearInterval(pollingRef.current)
          pollingRef.current = null
        }
      })
      .catch(() => {
        setIsCheckingAvailability(false)
      })
  }, [orderId, email])

  // Load full order data with sheets/poses
  useEffect(() => {
    Order
      .includes({
        cart: {
          orderPackages: [
            { addonSheets: [{ pose: 'studentImage' }, 'background'] },
            'background',
            'package',
            'student',
            'studentImage',
            { sheets: ['studentImage', 'background', 'foreground', { pose: 'studentImage' }] },
            { api_option: 'image_types' },
            { extras: 'prices' },
          ],
        },
      })
      .where({ for_email: [orderId, email, true] })
      .first()
      .then((response) => {
        if (!response.data) {
          setHasLoaded(true)
          return
        }

        let purchasedImages = []
        let purchasedBackgrounds = []
        let purchasedOrderPackages = []

        response.data.cart.orderPackages.forEach((orderPackage) => {
          if (orderPackage.hasDownloadableImages) {
            const sheets = orderPackage.sheets.filter((sheet) => sheet.hasDownloadableImage)
            if (sheets.length > 0) {
              sheets.forEach((sheet) => {
                if (sheet.pose) {
                  purchasedImages.push({ ...sheet.pose, foregroundId: sheet.foregroundId, foreground: sheet.foreground, isPNG: sheet.pose.isPNG, resourceIdentifier: { type: 'poses' } })
                }
                if (sheet.studentImage) {
                  purchasedImages.push(sheet.studentImage)
                }
                purchasedBackgrounds.push(sheet.background)
                purchasedOrderPackages.push(orderPackage)
              })
            } else if (orderPackage.sheets.length > 0) {
              const sheet = orderPackage.sheets[0]
              if (sheet.pose) {
                purchasedImages.push({ ...sheet.pose, foregroundId: sheet.foregroundId, foreground: sheet.foreground, isPNG: sheet.pose.isPNG, resourceIdentifier: { type: 'poses' } })
              }
              if (sheet.studentImage) {
                purchasedImages.push(sheet.studentImage)
              }
              purchasedBackgrounds.push(sheet.background)
              purchasedOrderPackages.push(orderPackage)
            }

            const downloadableAddonSheets = orderPackage.addonSheets.filter((addonSheet) => addonSheet.hasDownloadableImage)
            if (downloadableAddonSheets.length > 0) purchasedOrderPackages.push(orderPackage)
            downloadableAddonSheets.forEach((addonSheet) => {
              purchasedImages.push(addonSheet.pose)
              if (addonSheet.background) purchasedBackgrounds.push(addonSheet.background)
            })
          }

          if (orderPackage.studentImage && orderPackage.studentImage.imageUrl && orderPackage.hasDownloadableImages) {
            purchasedImages.push(orderPackage.studentImage)
            purchasedBackgrounds.push(orderPackage.background)
            purchasedOrderPackages.push(orderPackage)
          }
        })

        purchasedBackgrounds = purchasedBackgrounds.filter(b => !!b)

        setImages(purchasedImages)
        setBackgrounds(purchasedBackgrounds)
        setOrderPackages(purchasedOrderPackages)
        setHasLoaded(true)
      })
  }, [orderId, email])

  // Start availability checking and polling once images are loaded
  useEffect(() => {
    if (images.length === 0) return

    const items = buildItemsFromImages(images)
    checkAvailability(items)

    pollingRef.current = setInterval(() => {
      checkAvailability(items)
    }, 30000)

    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current)
        pollingRef.current = null
      }
    }
  }, [images, checkAvailability])

  if (!hasLoaded) {
    return (
      <div className={styles.container}>
        <div style={{ textAlign: 'center', padding: '20px' }}><Spinner /></div>
      </div>
    )
  }

  if (images.length === 0) return null

  const readyCount = Object.values(availability).filter((v) => v === true).length
  const totalCount = images.length
  const allReady = totalCount > 0 && readyCount === totalCount

  return (
    <div className={styles.container}>
      <Typography align="center" classes={styles.header}>
        Your Digital Downloads
      </Typography>

      <div className={styles.progressContainer}>
        {isCheckingAvailability ? (
          <div className={styles.progressLabel}>Checking image availability...</div>
        ) : allReady ? (
          <div className={styles.allReady}>All images ready for download</div>
        ) : (
          <>
            <div className={styles.progressLabel}>{readyCount} of {totalCount} images ready</div>
            <div className={styles.progressTrack}>
              <div
                className={styles.progressFill}
                style={{ width: `${(readyCount / totalCount) * 100}%` }}
              />
            </div>
            <div className={styles.progressHint}>This page will automatically check for new images every 30 seconds.</div>
          </>
        )}
      </div>

      {isCheckingAvailability && <div style={{ textAlign: 'center', padding: '20px' }}><Spinner /></div>}

      {!isCheckingAvailability && (
        <div className={styles.imageGrid}>
          {images.map((image, i) => {
            const itemKey = getItemKey(image)
            const isReady = availability[itemKey] === true
            const orderPackage = orderPackages[i] || orderPackages[0]
            const background = backgrounds[i]
            const downloadUrl = getDownloadUrl(image, orderPackage, background, email)

            return (
              <div key={`${itemKey}-${i}`} className={styles.imageItem}>
                <div className={styles.previewWrapper}>
                  <PreviewImage
                    actualForegroundUrl={image.foregroundId ? image.foreground?.imageUrl : undefined}
                    foregroundUrl={image.imageUrl}
                    backgroundUrl={background ? background.imageUrl : ''}
                    accessCodeOnly={false}
                  />
                </div>
                {isReady ? (
                  <a style={{ display: 'block' }} href={downloadUrl}>
                    <Button fullWidth variant="primary">
                      Download Image
                    </Button>
                  </a>
                ) : (
                  <Button fullWidth variant="primary" disabled>
                    Preparing Image...
                  </Button>
                )}
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
